import React from 'react';
type Ratio = '16/9' | '4/3' | '3/2' | '1/1' | '4/5' | '2/3';
interface ImageBoxProps {
  src?: string;
  /** The actual alt string, or empty for a genuinely decorative image. */
  alt: string;
  ratio?: Ratio;
  /** Hero images are eager with high priority; everything else is lazy. */
  priority?: boolean;
  /** `contain` for objects and products, `cover` for photography. */
  fit?: 'cover' | 'contain';
  className?: string;
  imageClassName?: string;
  /** Shown when there is no `src`. Keeps the reserved box honest. */
  placeholderLabel?: string;
}
const RATIO_CLASS: Record<Ratio, string> = {
  '16/9': 'aspect-[16/9]',
  '4/3': 'aspect-[4/3]',
  '3/2': 'aspect-[3/2]',
  '1/1': 'aspect-square',
  '4/5': 'aspect-[4/5]',
  '2/3': 'aspect-[2/3]'
};

/**
 * Reserves an aspect ratio so nothing shifts as the image loads, and gives
 * missing imagery an honest placeholder rather than a collapsed box.
 */
export function ImageBox({
  src,
  alt,
  ratio = '3/2',
  priority = false,
  fit = 'cover',
  className = '',
  imageClassName = '',
  placeholderLabel = 'Awaiting image'
}: ImageBoxProps) {
  return <div data-component-id="C05" className={`relative overflow-hidden bg-sand ${RATIO_CLASS[ratio]} ${className}`} data-id="element-439" data-mp="2gtahpuztl3s4">
      {src ? <img src={src} alt={alt} loading={priority ? 'eager' : 'lazy'} decoding={priority ? 'sync' : 'async'}
    // fetchpriority is valid HTML; React passes unknown lowercase
    // attributes straight through to the DOM.
    {...{
      fetchpriority: priority ? 'high' : undefined
    }} className={`absolute inset-0 h-full w-full ${fit === 'cover' ? 'object-cover' : 'object-contain p-[10%]'} ${imageClassName}`} data-id="element-440" data-mp="2cqdsvmjosbfq" /> :
    // Hidden from assistive technology on purpose: the surrounding
    // content already explains that photography is pending, and
    // announcing "Awaiting image" once per card would bury the names.
    <div className="absolute inset-0 flex items-center justify-center" aria-hidden="true" data-id="element-441" data-mp="1glwzx8hcydwo">
          <span className="text-xs font-bold uppercase tracking-[0.2em] text-ink-muted" data-id="element-442" data-mp="1ljygnmrba14z">
            {placeholderLabel}
          </span>
        </div>}
    </div>;
}